import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2, BookOpen, Users, Heart, Shield, UserCog, Briefcase } from "lucide-react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";

const roles = [
  { value: "student", label: "Student", icon: BookOpen },
  { value: "faculty", label: "Faculty", icon: Users },
  { value: "parent", label: "Parent", icon: Heart },
  { value: "admin", label: "Admin", icon: Shield },
  { value: "hr_manager", label: "HR Manager", icon: UserCog },
  { value: "placement_officer", label: "Placement", icon: Briefcase },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { document.title = "Create Account — Acadence"; }, []);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "student", phone: "", department: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      await register(form);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left — Dark editorial panel */}
      <div className="hidden lg:flex lg:w-5/12 bg-[#0F1B3D] noise-bg flex-col justify-between p-12">
        <Link to="/" className="flex items-center gap-2.5">
          <svg viewBox="0 0 32 32" fill="none" className="w-8 h-8">
            <rect x="1" y="21" width="8" height="8" rx="2" fill="#2A3A66"/>
            <rect x="12" y="13" width="8" height="8" rx="2" fill="#4B7BF5" opacity="0.5"/>
            <rect x="22" y="5" width="8" height="8" rx="2" fill="#4B7BF5"/>
          </svg>
          <span className="font-['DM_Sans'] font-medium text-lg text-[#EEEADF] tracking-[0.08em]">acadence</span>
        </Link>

        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#4B7BF5] mb-6">Join the Platform</p>
          <h2 className="font-['Instrument_Serif'] text-[2.6rem] text-[#EEEADF] leading-[1.05]">
            Your institute.<br />
            <em className="italic text-[#4B7BF5]">Fully digital.</em>
          </h2>
          <div className="mt-10 space-y-3">
            {["21 modules, one login", "Role-based access control", "Live in under 24 hours"].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-[#4B7BF5] flex-shrink-0" />
                <span className="font-['DM_Sans'] text-[#EEEADF]/40 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-[#1E2D55] pt-6">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.3em] text-[#EEEADF]/20">© 2026 Acadence</p>
        </div>
      </div>

      {/* Right — Form */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.45 }}
        className="flex-1 bg-[#F5F2EA] flex items-center justify-center p-8 overflow-y-auto"
      >
        <div className="w-full max-w-sm py-8">
          {/* Mobile logo */}
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10">
            <svg viewBox="0 0 32 32" fill="none" className="w-8 h-8">
              <rect x="1" y="21" width="8" height="8" rx="2" fill="#8B95A8"/>
              <rect x="12" y="13" width="8" height="8" rx="2" fill="#4B7BF5" opacity="0.5"/>
              <rect x="22" y="5" width="8" height="8" rx="2" fill="#4B7BF5"/>
            </svg>
            <span className="font-['DM_Sans'] font-medium text-lg text-[#0F1B3D] tracking-[0.08em]">acadence</span>
          </Link>

          <h1 className="font-['Syne'] font-bold text-3xl text-[#0F1B3D] mb-1.5">Create account</h1>
          <p className="font-['DM_Sans'] text-[#7A7468] text-sm mb-8">Join your institute on Acadence</p>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                data-testid="register-error"
                className="mb-5 px-4 py-3 bg-[#FF3636]/8 border border-[#FF3636]/20 text-[#CC2020] text-sm font-['DM_Sans']"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
            {/* Role selector */}
            <div>
              <label className="block font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-3">I am a...</label>
              <div className="grid grid-cols-3 gap-2" data-testid="role-selector">
                {roles.map(r => (
                  <button
                    key={r.value}
                    type="button"
                    data-testid={`role-${r.value}`}
                    onClick={() => setForm(p => ({ ...p, role: r.value }))}
                    className={`flex flex-col items-center gap-1.5 py-3 px-2 border transition-all ${
                      form.role === r.value
                        ? "border-[#0F1B3D] bg-[#0F1B3D] text-[#4B7BF5]"
                        : "border-[#D8D4CB] bg-transparent text-[#7A7468] hover:border-[#0F1B3D] hover:text-[#0F1B3D]"
                    }`}
                  >
                    <r.icon className="w-4 h-4" strokeWidth={1.5} />
                    <span className="font-['Syne'] font-bold text-[9px] uppercase tracking-wide leading-tight text-center">{r.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className="block font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-3">Full Name</label>
                <input
                  type="text" name="name" value={form.name} onChange={handleChange} required
                  placeholder="Your name"
                  data-testid="register-name-input"
                  className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-3 text-[#0F1B3D] font-['DM_Sans'] text-sm focus:outline-none focus:border-[#0F1B3D] placeholder:text-[#BDBAA8] transition-colors"
                />
              </div>
              <div>
                <label className="block font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-3">Phone</label>
                <input
                  type="tel" name="phone" value={form.phone} onChange={handleChange}
                  placeholder="+91 98765..."
                  data-testid="register-phone-input"
                  className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-3 text-[#0F1B3D] font-['DM_Sans'] text-sm focus:outline-none focus:border-[#0F1B3D] placeholder:text-[#BDBAA8] transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-3">Email Address</label>
              <input
                type="email" name="email" value={form.email} onChange={handleChange} required
                placeholder="you@institute.edu"
                data-testid="register-email-input"
                className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-3 text-[#0F1B3D] font-['DM_Sans'] text-sm focus:outline-none focus:border-[#0F1B3D] placeholder:text-[#BDBAA8] transition-colors"
              />
            </div>

            <div>
              <label className="block font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-3">Department</label>
              <input
                type="text" name="department" value={form.department} onChange={handleChange}
                placeholder="e.g. Computer Science"
                data-testid="register-department-input"
                className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-3 text-[#0F1B3D] font-['DM_Sans'] text-sm focus:outline-none focus:border-[#0F1B3D] placeholder:text-[#BDBAA8] transition-colors"
              />
            </div>

            <div>
              <label className="block font-['Syne'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-3">Password</label>
              <input
                type="password" name="password" value={form.password} onChange={handleChange} required
                placeholder="Min. 6 characters"
                data-testid="register-password-input"
                className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-3 text-[#0F1B3D] font-['DM_Sans'] text-sm focus:outline-none focus:border-[#0F1B3D] placeholder:text-[#BDBAA8] transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#0F1B3D] text-[#4B7BF5] font-['Syne'] font-bold text-xs uppercase tracking-widest py-4 hover:bg-[#162044] transition-colors active:scale-95 disabled:opacity-50 mt-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create Account <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="font-['DM_Sans'] text-center text-sm text-[#7A7468] mt-6">
            Already have an account?{" "}
            <Link to="/login" data-testid="go-login-link" className="font-['Syne'] font-bold text-[#0F1B3D] hover:text-[#4B7BF5] hover:bg-[#0F1B3D] px-1.5 py-0.5 transition-all">
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
